// ---------------------------------------------------------------------------
// ExampleTable — generic chevron-expand "row per example" table.
//
// A renderer supplies:
//   - `rows`: an array of items (any shape)
//   - `columns`: how to render each cell from a row
//   - `expand?`: how to render the expanded body for a row (optional)
//
// Generic over the row type, so a KG renderer can pass `KgChannel`, a future
// prompt-based renderer can pass `PromptExample`, etc.
// ---------------------------------------------------------------------------

import { Button, mergeClasses } from '@fluentui/react-components';
import {
  ChevronDown16Regular,
  ChevronRight16Regular,
} from '@fluentui/react-icons';
import { useState } from 'react';

import type {
  ExampleTableColumn,
  ExampleTableProps,
} from './ExampleTable.interface.js';
import { useStyles } from './ExampleTable.style.js';

export type {
  ExampleTableColumn,
  ExampleTableProps,
} from './ExampleTable.interface.js';

export function ExampleTable<T>({
  rows,
  columns,
  rowKey,
  expand,
}: ExampleTableProps<T>) {
  const styles = useStyles();
  const expandable = expand !== undefined;

  return (
    <table className={styles.table}>
      <thead>
        <tr className={styles.headerRow}>
          {expandable && <th className={styles.chevronCell} aria-hidden />}
          <th className={mergeClasses(styles.headerCell, styles.indexCell)}>
            #
          </th>
          {columns.map((c) => (
            <th
              key={c.key}
              className={styles.headerCell}
              style={c.width ? { width: c.width } : undefined}
            >
              {c.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, idx) => (
          <ExampleTableRow
            key={rowKey(row, idx)}
            row={row}
            index={idx}
            columns={columns}
            expand={expand}
          />
        ))}
      </tbody>
    </table>
  );
}

function ExampleTableRow<T>({
  row,
  index,
  columns,
  expand,
}: {
  row: T;
  index: number;
  columns: ExampleTableColumn<T>[];
  expand?: (row: T, index: number) => React.ReactNode;
}) {
  const styles = useStyles();
  const [isExpanded, setIsExpanded] = useState(false);
  const expandable = expand !== undefined;
  const colSpan = columns.length + 1 + (expandable ? 1 : 0);

  return (
    <>
      <tr
        className={mergeClasses(
          styles.row,
          isExpanded ? styles.rowExpanded : undefined,
        )}
      >
        {expandable && (
          <td className={styles.chevronCell}>
            <Button
              appearance="subtle"
              size="small"
              icon={
                isExpanded ? (
                  <ChevronDown16Regular />
                ) : (
                  <ChevronRight16Regular />
                )
              }
              onClick={() => setIsExpanded((v) => !v)}
              aria-expanded={isExpanded}
              aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
            />
          </td>
        )}
        <td className={mergeClasses(styles.cell, styles.indexCell)}>
          {index + 1}
        </td>
        {columns.map((c) => (
          <td key={c.key} className={styles.cell}>
            {c.render(row, index)}
          </td>
        ))}
      </tr>
      {isExpanded && expand !== undefined && (
        <tr className={styles.expansionRow}>
          <td className={styles.expansionCell} colSpan={colSpan}>
            {expand(row, index)}
          </td>
        </tr>
      )}
    </>
  );
}
